import type {
    GetTileDataOptions,
    ImageTileIndex,
    ImageTileRawData,
    ImageTilesDescriptor,
    TiledImageBase,
    TiledImageDataCache,
    TiledImageManagerBase,
} from "@cm/utils"
import {isPromise, TiledImageIndex} from "@cm/utils"

export class TiledImage implements TiledImageBase<ImageBitmap> {
    static async create(imageTilesDescriptor: ImageTilesDescriptor, tiledImageManager: TiledImageManagerBase<TiledImage>): Promise<TiledImage> {
        return new TiledImage(imageTilesDescriptor, tiledImageManager.dataCache, tiledImageManager.cacheInMemory)
    }

    get tileIndex(): TiledImageIndex {
        return this._tileIndex
    }

    isValidTile(imageTileIndex: ImageTileIndex): boolean {
        return this._tileIndex.isValidTileIndex(imageTileIndex)
    }

    async getTileData(imageTileIndex: ImageTileIndex, options?: GetTileDataOptions): Promise<ImageBitmap> {
        const imageTileId =
            imageTileIndex.mipLevel * this._tileIndex.numTilesX * this._tileIndex.numTilesY +
            imageTileIndex.tileIndex.y * this._tileIndex.numTilesX +
            imageTileIndex.tileIndex.x
        let tileData = this._tileDataByTileId.get(imageTileId)
        if (!tileData) {
            const promisedTileData = this.createTileData(imageTileIndex, options)
            this._tileDataByTileId.set(imageTileId, promisedTileData)
            try {
                tileData = await promisedTileData
            } catch (error) {
                this._tileDataByTileId.delete(imageTileId)
                throw error
            }
            if (this._cacheInMemory) {
                this._tileDataByTileId.set(imageTileId, tileData)
            } else {
                this._tileDataByTileId.delete(imageTileId)
            }
        } else if (isPromise(tileData)) {
            tileData = await tileData
        }
        return tileData
    }

    private async createTileData(imageTileIndex: ImageTileIndex, options?: GetTileDataOptions): Promise<ImageBitmap> {
        const imageTileRawData = await this.getRawTileData(imageTileIndex, options)
        return await createImageBitmap(new Blob([imageTileRawData.encodedData], {type: this.imageTilesDescriptor.tileMediaType}))
    }

    private getRawTileData(imageTileIndex: ImageTileIndex, options?: GetTileDataOptions): Promise<ImageTileRawData> {
        const url = this.getTileDownloadUrl(imageTileIndex)
        return this.imageTileDataCache.getRawTileData(url, options)
    }

    private constructor(
        readonly imageTilesDescriptor: ImageTilesDescriptor,
        private imageTileDataCache: TiledImageDataCache,
        private _cacheInMemory: boolean,
    ) {
        this._tileIndex = TiledImageIndex.fromDescriptor(imageTilesDescriptor)
    }

    private getTileDownloadUrl(imageTileIndex: ImageTileIndex) {
        return this.imageTilesDescriptor.tileDownloadUrlPrefix + this.tileIndex.getTileFilename(imageTileIndex)
    }

    private readonly _tileIndex: TiledImageIndex
    private _tileDataByTileId = new Map<number, ImageBitmap | Promise<ImageBitmap>>()
}
